import * as React from 'react';
import { produce } from 'immer';
import { message } from 'antd';
import { useRequest } from 'ahooks';
import { keys, map, find } from 'lodash-es';
import { getConfigurationScenarioTemplate } from '../service';
import type {
  PostConfigurationPolicyData,
  GetConfigurationScenarioTemplateReturn,
  BootProcessingConfigType,
} from '../types/api';
import { useConfigurationId } from '../hooks/useConfigurationId';
import { useConfigStore } from '../hooks/useConfigStore';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import type { Optional } from '../types/alias';
import { useTranslate } from '@tolgee/react';
import {
  advancedScenarioMap,
  aiSaleBotScenarioMap,
  arrayScenario,
  basicScenarioMap,
  marketingScenarioMap,
} from '../consts/scenarioMap';
import { useStepName } from '../hooks/useStepName';
import { useGetCCV } from '../hooks/useConfiguration';
import { useMemo } from 'react';
import { regionTransLangMap as _regionTransLangMap } from 'views/Aide/Scenario/PolicyForm/TranslateTool/useTargetLang';
import toLowerCase from 'utils/toLowerCase';

const regionTransLangMap = {
  ..._regionTransLangMap,
  ph: 'ph',
  my: 'ms',
};

type ExecuteContext =
  PostConfigurationPolicyData['scenarios'][number]['regionPolicyList'][number]['executeContext'];

type ScenarioFormContextProps = {
  value: PostConfigurationPolicyData;
  cacheKey: string;
  getExecuteContext: (scenario: string, region: string, isMustache?: boolean) => ExecuteContext;
  setExecuteContext: (scenario: string, region: string, value: Optional<ExecuteContext>) => void;
};

const ScenarioFormContext = React.createContext<ScenarioFormContextProps>(null!);

export const useScenarioFormContext = () => {
  const context = React.useContext(ScenarioFormContext);
  return context;
};

export const getRegionTemplate = (
  data: GetConfigurationScenarioTemplateReturn,
  scenarioType: string,
  regionOrLang: string,
) => {
  const lang =
    regionTransLangMap[toLowerCase(regionOrLang as General.Region)] || regionOrLang || 'en-US';
  const content = find(
    data?.scenarioTemplates || [],
    (item) => item.scenarioType === scenarioType,
  )?.multiLingualTemplates.find((item) => item.lang === lang)?.template;
  return content || '';
};

export const getRegionTemplateTranslate = (
  data: GetConfigurationScenarioTemplateReturn,
  scenarioType: string,
  region: string,
  lang: string,
) => {
  const targetLang = regionTransLangMap[toLowerCase(region as General.Region)] || region || 'en-US';
  const target = find(
    data?.scenarioTemplates || [],
    (item) => item.scenarioType === scenarioType,
  )?.multiLingualTemplates.find((item) => item.lang === targetLang);
  return target?.translateMap?.[lang as 'en-US' | 'zh-CN'] || '';
};

export const ScenarioFormProvider = (props: {
  configType?: BootProcessingConfigType;
  children: React.ReactNode;
}) => {
  const { configType = 'BASIC', children } = props;
  const { t } = useTranslate();
  const stepName = useStepName();
  const cacheKey = `scenarioFormContext-${configType}-${stepName}`;
  const cacheScenarios = useGetCCV(cacheKey);
  const scenario = useMemo(() => {
    return arrayScenario(
      configType === 'BASIC'
        ? basicScenarioMap[stepName]
        : configType === 'MARKETING'
        ? marketingScenarioMap[stepName]
        : configType === 'AI_SALE_BOT'
        ? aiSaleBotScenarioMap[stepName]
        : advancedScenarioMap[stepName],
    );
  }, [configType, stepName]);
  const { stores, regionsMap, storeIncludesTiktok } = useConfigStore();
  const configurationId = useConfigurationId();
  const lang = useSystemLanguageValue();
  const [value, setValue] = React.useState<PostConfigurationPolicyData['scenarios']>(
    cacheScenarios || [],
  );

  const { loading } = useRequest(
    () =>
      getConfigurationScenarioTemplate({
        scenarioTypes: scenario,
        platform: storeIncludesTiktok ? 'TIKTOK' : 'SHOPEE',
      }),
    {
      ready: !cacheScenarios && !!scenario && !!stores && scenario.length > 0,
      refreshDeps: [scenario],
      onSuccess: (data) => {
        setValue(
          map(scenario, (item) => {
            return {
              scenarioType: item,
              regionPolicyList: keys(regionsMap).map((region) => {
                return {
                  enable: true,
                  region,
                  executeContext: {
                    content: getRegionTemplate(data, item, region),
                    translatedContent: getRegionTemplateTranslate(data, item, region, lang),
                  },
                };
              }),
              stores,
            };
          }),
        );
      },
      onError: () => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      },
    },
  );

  const getExecuteContext = React.useCallback(
    (scenario: string, region: string, isMustache = false) => {
      const executeContext = find(
        value,
        (item) => item.scenarioType === scenario,
      )?.regionPolicyList?.find((each) => each.region === region)?.executeContext;

      return executeContext
        ? isMustache
          ? executeContext
          : extractTextFromExecuteContext(executeContext)
        : { content: '', translatedContent: '' };
    },
    [value],
  );

  const setExecuteContext = React.useCallback(
    (scenario: string, region: string, value: Optional<ExecuteContext>) => {
      setValue(
        produce((draft) => {
          const target = find(
            draft,
            (item) => item.scenarioType === scenario,
          )?.regionPolicyList?.find((each) => each.region === region);

          if (target) {
            const targetContent = target.executeContext;
            if (targetContent.content !== mustacheExecuteContext(value).content) {
              target.executeContext = {
                ...target.executeContext,
                ...mustacheExecuteContext({ translatedContent: '', ...value }),
              };
            }
          }
        }),
      );
    },
    [],
  );

  if (loading || value.length === 0) return null;

  return (
    <ScenarioFormContext.Provider
      value={{
        value: {
          scenarios: value,
          configurationId,
        },
        getExecuteContext,
        setExecuteContext,
        cacheKey,
      }}
    >
      {children}
    </ScenarioFormContext.Provider>
  );
};

export const extractText = (template: string | undefined) => {
  const regex = /.*\{\{#text\}\}([\s\S]*)\{\{\/text\}\}/;
  const match = (template || '').match(regex);

  let extractedText = '';
  if (match) {
    extractedText = match[1];
  } else {
    extractedText = template || '';
  }
  return extractedText;
};

export const mustacheText = (template: string | undefined) => {
  return template?.startsWith('#mustache#')
    ? template
    : `#mustache#{{#text}}${template || ''}{{/text}}`;
};

const extractTextFromExecuteContext = (executeContext: ExecuteContext) => {
  const { content: _content, translatedContent: _translatedContent } = executeContext;
  const content = extractText(_content);
  const translatedContent = extractText(_translatedContent);
  return {
    content,
    translatedContent,
  };
};

const mustacheExecuteContext = (executeContext: Optional<ExecuteContext>) => {
  const copy = { ...executeContext };
  for (const key in copy) {
    if (key === 'content' || key === 'translatedContent') {
      copy[key] = mustacheText(copy[key]);
    }
  }
  return copy;
};
